import { useEffect, useRef, useState } from "react";
import api from "@/lib/api";
import {
  Save, Plus, Trash2, ChevronDown, ChevronRight,
  ExternalLink, Upload, Eye, Palette, Layers, MessageSquare, Star, HelpCircle,
  Sparkles, BarChart3, Zap, Image as ImageIcon, FileText, Link2, ArrowUp, ArrowDown,
} from "lucide-react";
import { COMPONENT_TYPES, DEFAULTS } from "@/components/builder/BlockRenderer";
import { TemplatePicker } from "@/components/TemplatePicker";
import IconPicker from "@/components/IconPicker";
import { HEADING_FONTS, BODY_FONTS } from "@/lib/platformFonts";
import AdminLayout from "@/components/AdminLayout";

/* -------------------------------------------------------------------------- */
/*  Super-Admin: Landing-Page Editor                                          */
/* -------------------------------------------------------------------------- */

const SECTIONS = [
  { key: "logo", label: "Logo", icon: ImageIcon },
  { key: "colors", label: "Farben", icon: Palette },
  { key: "typography", label: "Schriftarten", icon: FileText },
  { key: "seo", label: "SEO Meta", icon: FileText },
  { key: "hero", label: "Hero-Bereich", icon: Sparkles },
  { key: "trust", label: "Trust-Bar (Statistiken)", icon: BarChart3 },
  { key: "features", label: "Funktionen", icon: Layers },
  { key: "benefits", label: "Vorteile", icon: Zap },
  { key: "pricing", label: "Pakete-Teaser", icon: Link2 },
  { key: "testimonials", label: "Stimmen", icon: MessageSquare },
  { key: "faq", label: "FAQ", icon: HelpCircle },
  { key: "blocks", label: "Eigene Blöcke (Elementor-Style)", icon: Layers },
  { key: "cta_final", label: "Final-CTA", icon: Star },
  { key: "footer", label: "Footer", icon: FileText },
];

export default function LandingEditor() {
  const [content, setContent] = useState(null);
  const [open, setOpen] = useState({ colors: true });
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [err, setErr] = useState("");

  const load = async () => {
    const { data } = await api.get("/admin/platform-content");
    setContent(data);
  };
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!content) return;
    setSaving(true); setErr("");
    try {
      const { id, _id, created_at, updated_at, ...payload } = content;
      const { data } = await api.put("/admin/platform-content", payload);
      setContent(data);
      setSavedAt(new Date().toLocaleTimeString("de-DE"));
    } catch (e) {
      setErr(e.response?.data?.detail || "Speichern fehlgeschlagen");
    } finally { setSaving(false); }
  };

  const set = (path, value) => {
    setContent((prev) => {
      const next = JSON.parse(JSON.stringify(prev));
      const parts = path.split(".");
      let cur = next;
      for (let i = 0; i < parts.length - 1; i++) {
        if (!(parts[i] in cur) || typeof cur[parts[i]] !== "object" || cur[parts[i]] == null) cur[parts[i]] = {};
        cur = cur[parts[i]];
      }
      cur[parts[parts.length - 1]] = value;
      return next;
    });
  };

  const uploadImage = async (file, targetPath) => {
    const fd = new FormData();
    fd.append("file", file);
    const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
    set(targetPath, data.url);
  };

  if (!content) {
    return <div style={{ padding: 48, color: "var(--mv-text-muted)" }}>Lade ...</div>;
  }

  return (
    <AdminLayout>
      <div
        className="sticky top-0 z-30 flex items-center justify-between gap-3 flex-wrap px-6 py-4"
        style={{ background: "var(--mv-bg)", borderBottom: "1px solid var(--mv-border)" }}
      >
        <div>
          <p className="mv-label mb-1">Plattform-Inhalt</p>
          <h1 className="font-display text-2xl">Landing-Page Editor</h1>
        </div>
        <div className="flex items-center gap-3">
          {savedAt && <span className="text-xs" style={{ color: "var(--mv-text-muted)" }}>Gespeichert um {savedAt}</span>}
          <a href="/" target="_blank" rel="noreferrer" className="mv-btn-ghost text-sm" data-testid="open-landing-preview">
            <Eye className="w-4 h-4" strokeWidth={1.5} /> Vorschau
          </a>
          <button data-testid="landing-save-btn" onClick={save} disabled={saving} className="mv-btn">
            <Save className="w-4 h-4" strokeWidth={1.5} /> {saving ? "Speichert…" : "Speichern"}
          </button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-10">
        <div className="mb-8">
          <p className="text-sm" style={{ color: "var(--mv-text-muted)" }}>
            Alle Inhalte der öffentlichen StudioMV-Landingpage. Änderungen sind sofort live, sobald du speicherst.
          </p>
        </div>

        {err && <p className="text-sm mb-4 px-3 py-2 rounded bg-red-50 text-red-700">{err}</p>}

        <div className="space-y-3">
          {SECTIONS.map(({ key, label, icon: Icon }) => {
            const isOpen = !!open[key];
            return (
              <div key={key} className="mv-card overflow-hidden" data-testid={`section-${key}`}>
                <button
                  onClick={() => setOpen({ ...open, [key]: !isOpen })}
                  data-testid={`section-toggle-${key}`}
                  className="w-full flex items-center justify-between px-5 py-4 hover:bg-[var(--mv-surface)] transition-colors"
                >
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4" strokeWidth={1.5} style={{ color: "var(--mv-primary)" }} />
                    <span className="text-sm font-medium">{label}</span>
                  </div>
                  {isOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
                </button>
                {isOpen && (
                  <div className="px-5 pb-5 border-t" style={{ borderColor: "var(--mv-border)" }}>
                    <div className="pt-5">
                      <SectionEditor sectionKey={key} content={content} set={set} uploadImage={uploadImage} />
                    </div>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </AdminLayout>
  );
}

/* ========== per-section editors ========== */

function SectionEditor({ sectionKey, content, set, uploadImage }) {
  switch (sectionKey) {
    case "logo":     return <LogoEditor value={content.logo_url} set={set} uploadImage={uploadImage} />;
    case "colors":   return <ColorsEditor c={content.colors || {}} set={set} />;
    case "typography": return <TypographyEditor c={content.typography || {}} set={set} />;
    case "seo":      return <SeoEditor c={content.seo || {}} set={set} uploadImage={uploadImage} />;
    case "hero":     return <HeroEditor c={content.hero || {}} set={set} uploadImage={uploadImage} />;
    case "trust":    return <TrustEditor c={content.trust || {}} set={set} />;
    case "features": return <FeaturesEditor c={content.features || {}} set={set} />;
    case "benefits": return <BenefitsEditor c={content.benefits || {}} set={set} />;
    case "pricing":  return <PricingTeaserEditor c={content.pricing || {}} set={set} />;
    case "testimonials": return <TestimonialsEditor c={content.testimonials || {}} set={set} uploadImage={uploadImage} />;
    case "faq":      return <FaqEditor c={content.faq || {}} set={set} />;
    case "blocks":   return <BlocksEditor blocks={content.blocks || []} set={set} uploadImage={uploadImage} />;
    case "cta_final":return <CtaFinalEditor c={content.cta_final || {}} set={set} />;
    case "footer":   return <FooterEditor c={content.footer || {}} set={set} />;
    default: return null;
  }
}

function Field({ label, hint, children }) {
  return (
    <div>
      <label className="block text-xs font-medium mb-1.5">{label}</label>
      {children}
      {hint && <p className="text-[11px] mt-1" style={{ color: "var(--mv-text-muted)" }}>{hint}</p>}
    </div>
  );
}

function LogoEditor({ value, set, uploadImage }) {
  return (
    <div>
      <p className="text-xs mb-4" style={{ color: "var(--mv-text-muted)" }}>
        Dieses Logo ersetzt das Standard-„StudioMV"-Textzeichen einheitlich auf der gesamten Plattform —
        Login, Registrierung, Preise-Seite, Super-Admin- und Salon-CMS-Menü sowie der Startseite.
        Ohne eigenes Logo wird weiterhin das Standard-Zeichen angezeigt.
      </p>
      <Field label="Plattform-Logo">
        <ImageInput value={value} onChange={(v) => set("logo_url", v)} uploadImage={(f) => uploadImage(f, "logo_url")} />
      </Field>
    </div>
  );
}

function ColorsEditor({ c, set }) {
  const COLOR_FIELDS = [
    ["primary", "Primär (z. B. Buttons, Logo, Footer-BG)"],
    ["bg", "Hintergrund"],
    ["text", "Text"],
    ["accent", "Akzent (Türkis)"],
    ["accent2", "Akzent 2 (Cyan)"],
    ["muted", "Hilfstext / Muted"],
  ];
  return (
    <div className="grid sm:grid-cols-2 gap-4">
      {COLOR_FIELDS.map(([key, label]) => (
        <Field key={key} label={label}>
          <div className="flex items-center gap-3">
            <input
              type="color" data-testid={`color-${key}`}
              value={c[key] || "#000000"}
              onChange={(e) => set(`colors.${key}`, e.target.value)}
              className="w-14 h-11 rounded border cursor-pointer"
            />
            <input
              className="mv-input font-mono text-sm"
              value={c[key] || ""}
              onChange={(e) => set(`colors.${key}`, e.target.value)}
            />
          </div>
        </Field>
      ))}
    </div>
  );
}

function TypographyEditor({ c, set }) {
  const heading = c.heading_font || "Cormorant Garamond";
  const body = c.body_font || "Manrope";
  return (
    <div className="space-y-6">
      <Field
        label="Überschriften-Schriftart"
        hint="Wird als Standard für alle Headings auf der Landing Page und in Plattform-Seiten verwendet (kann pro Heading überschrieben werden)."
      >
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="heading-font-picker">
          {HEADING_FONTS.map((f) => {
            const active = heading === f.value;
            return (
              <button
                key={f.value}
                type="button"
                data-testid={`heading-font-${f.value.replace(/\s+/g, "-")}`}
                onClick={() => set("typography.heading_font", f.value)}
                className="p-3 text-left rounded-lg border transition-all"
                style={{
                  borderColor: active ? "var(--mv-primary)" : "var(--mv-border)",
                  background: active ? "rgba(20,184,166,0.06)" : "transparent",
                  fontFamily: `'${f.value}', serif`,
                }}
              >
                <div className="text-xl leading-none mb-1">Aa</div>
                <div className="text-[10px] uppercase tracking-wider opacity-60" style={{ fontFamily: "inherit" }}>{f.category}</div>
                <div className="text-xs mt-1" style={{ fontFamily: "Manrope, sans-serif" }}>{f.label}</div>
              </button>
            );
          })}
        </div>
      </Field>

      <Field label="Fließtext-Schriftart" hint="Für Untertitel, Absätze, Buttons.">
        <div className="grid grid-cols-2 sm:grid-cols-3 gap-2" data-testid="body-font-picker">
          {BODY_FONTS.map((f) => {
            const active = body === f.value;
            return (
              <button
                key={f.value}
                type="button"
                data-testid={`body-font-${f.value.replace(/\s+/g, "-")}`}
                onClick={() => set("typography.body_font", f.value)}
                className="p-3 text-left rounded-lg border transition-all"
                style={{
                  borderColor: active ? "var(--mv-primary)" : "var(--mv-border)",
                  background: active ? "rgba(20,184,166,0.06)" : "transparent",
                  fontFamily: `'${f.value}', sans-serif`,
                }}
              >
                <div className="text-sm leading-tight">The quick brown fox</div>
                <div className="text-xs mt-1 opacity-60" style={{ fontFamily: "Manrope, sans-serif" }}>{f.label}</div>
              </button>
            );
          })}
        </div>
      </Field>
    </div>
  );
}

function SeoEditor({ c, set, uploadImage }) {
  return (
    <div className="space-y-4">
      <Field label="Seitentitel (Browser-Tab + Google)">
        <input data-testid="seo-title" className="mv-input" value={c.title || ""} onChange={(e) => set("seo.title", e.target.value)} />
      </Field>
      <Field label="Meta-Description (max ~160 Zeichen)" hint={`${(c.description || "").length} / 160`}>
        <textarea data-testid="seo-description" className="mv-input min-h-[80px]" value={c.description || ""} onChange={(e) => set("seo.description", e.target.value)} />
      </Field>
      <Field label="Open-Graph Bild (1200×630)">
        <ImageInput value={c.og_image} onChange={(v) => set("seo.og_image", v)} uploadImage={(f) => uploadImage(f, "seo.og_image")} />
      </Field>
    </div>
  );
}

function HeroEditor({ c, set, uploadImage }) {
  const layout = c.layout || "split";
  const overlay = typeof c.overlay_opacity === "number" ? c.overlay_opacity : 0.55;
  const layoutOptions = [
    { value: "split", label: "Bild rechts neben Text", hint: "Klassisches Split-Layout. Text links, Bild als Card rechts." },
    { value: "background", label: "Bild im Hintergrund (Full-Width)", hint: "Bild als breites Banner-Hintergrund mit dunklem Overlay und hellem Text." },
    { value: "fullscreen", label: "Full-Window Bild (100vh)", hint: "Bild füllt den gesamten Viewport beim ersten Laden. Maximaler Wow-Effekt." },
  ];
  return (
    <div className="space-y-4">
      <Field label="Eyebrow (kleiner Text über der Headline)"><input data-testid="hero-eyebrow" className="mv-input" value={c.eyebrow || ""} onChange={(e) => set("hero.eyebrow", e.target.value)} /></Field>
      <Field label="Headline"><textarea data-testid="hero-headline" className="mv-input min-h-[64px]" value={c.headline || ""} onChange={(e) => set("hero.headline", e.target.value)} /></Field>
      <Field label="Subline"><textarea data-testid="hero-subline" className="mv-input min-h-[80px]" value={c.subline || ""} onChange={(e) => set("hero.subline", e.target.value)} /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="Primärer CTA — Label"><input className="mv-input" value={c.cta_primary?.label || ""} onChange={(e) => set("hero.cta_primary.label", e.target.value)} /></Field>
        <Field label="Primärer CTA — URL"><input className="mv-input font-mono text-sm" value={c.cta_primary?.url || ""} onChange={(e) => set("hero.cta_primary.url", e.target.value)} /></Field>
        <Field label="Sekundärer CTA — Label"><input className="mv-input" value={c.cta_secondary?.label || ""} onChange={(e) => set("hero.cta_secondary.label", e.target.value)} /></Field>
        <Field label="Sekundärer CTA — URL"><input className="mv-input font-mono text-sm" value={c.cta_secondary?.url || ""} onChange={(e) => set("hero.cta_secondary.url", e.target.value)} /></Field>
      </div>

      <Field label="Hero-Layout">
        <div className="grid sm:grid-cols-3 gap-2" data-testid="hero-layout-picker">
          {layoutOptions.map((opt) => {
            const active = layout === opt.value;
            return (
              <button
                key={opt.value}
                type="button"
                data-testid={`hero-layout-${opt.value}`}
                onClick={() => set("hero.layout", opt.value)}
                className="text-left p-3 rounded-lg border transition-all"
                style={{
                  borderColor: active ? "var(--mv-primary)" : "rgba(0,0,0,0.12)",
                  background: active ? "rgba(20,184,166,0.06)" : "transparent",
                  boxShadow: active ? "0 0 0 2px rgba(20,184,166,0.15)" : "none",
                }}
              >
                <div className="text-sm font-medium" style={{ color: "var(--mv-text)" }}>{opt.label}</div>
                <div className="text-xs mt-1 opacity-70" style={{ color: "var(--mv-text-muted)" }}>{opt.hint}</div>
              </button>
            );
          })}
        </div>
      </Field>

      <Field label={layout === "split" ? "Hero-Bild (rechts neben dem Text, optional)" : "Hero-Bild (Hintergrund, erforderlich für diesen Layout-Modus)"}>
        <ImageInput value={c.image_url} onChange={(v) => set("hero.image_url", v)} uploadImage={(f) => uploadImage(f, "hero.image_url")} />
      </Field>

      {(layout === "background" || layout === "fullscreen") && (
        <Field label={`Overlay-Dunkelheit (${Math.round(overlay * 100)}%)`}>
          <input
            type="range" min="0" max="0.9" step="0.05"
            data-testid="hero-overlay-opacity"
            value={overlay}
            onChange={(e) => set("hero.overlay_opacity", parseFloat(e.target.value))}
            className="w-full"
          />
          <p className="text-xs mt-1 opacity-60">Sorgt für lesbaren Text über dem Bild. Empfohlen: 40–60 %.</p>
        </Field>
      )}
    </div>
  );
}

function TrustEditor({ c, set }) {
  const items = c.stats || [];
  return (
    <div className="space-y-4">
      <Field label="Trust-Label (über den Zahlen)"><input className="mv-input" value={c.label || ""} onChange={(e) => set("trust.label", e.target.value)} /></Field>
      <ListEditor
        items={items} testid="trust"
        onAdd={() => set("trust.stats", [...items, { value: "100%", label: "Beispiel" }])}
        onRemove={(i) => set("trust.stats", items.filter((_, k) => k !== i))}
        renderRow={(it, i) => (
          <div className="grid sm:grid-cols-2 gap-2">
            <input placeholder="Wert (z. B. -30%)" className="mv-input" value={it.value || ""} onChange={(e) => set(`trust.stats.${i}.value`, e.target.value)} />
            <input placeholder="Label" className="mv-input" value={it.label || ""} onChange={(e) => set(`trust.stats.${i}.label`, e.target.value)} />
          </div>
        )}
      />
    </div>
  );
}

function FeaturesEditor({ c, set }) {
  const items = c.items || [];
  return (
    <div className="space-y-4">
      <Field label="Eyebrow"><input className="mv-input" value={c.eyebrow || ""} onChange={(e) => set("features.eyebrow", e.target.value)} /></Field>
      <Field label="Headline"><input className="mv-input" value={c.headline || ""} onChange={(e) => set("features.headline", e.target.value)} /></Field>
      <Field label="Subline"><textarea className="mv-input min-h-[60px]" value={c.subline || ""} onChange={(e) => set("features.subline", e.target.value)} /></Field>
      <ListEditor
        items={items} testid="features"
        onAdd={() => set("features.items", [...items, { icon: "Sparkles", title: "Neue Funktion", description: "Beschreibung..." }])}
        onRemove={(i) => set("features.items", items.filter((_, k) => k !== i))}
        renderRow={(it, i) => (
          <div className="grid sm:grid-cols-[1fr_2fr] gap-2">
            <IconPicker
              value={it.icon || ""}
              onChange={(v) => set(`features.items.${i}.icon`, v)}
              testId={`features-icon-${i}`}
              placeholder="Icon wählen"
            />
            <input placeholder="Titel" className="mv-input" value={it.title || ""} onChange={(e) => set(`features.items.${i}.title`, e.target.value)} />
            <textarea placeholder="Beschreibung" className="mv-input sm:col-span-2 min-h-[60px]" value={it.description || ""} onChange={(e) => set(`features.items.${i}.description`, e.target.value)} />
          </div>
        )}
      />
    </div>
  );
}

function BenefitsEditor({ c, set }) {
  const items = c.items || [];
  return (
    <div className="space-y-4">
      <Field label="Eyebrow"><input className="mv-input" value={c.eyebrow || ""} onChange={(e) => set("benefits.eyebrow", e.target.value)} /></Field>
      <Field label="Headline"><input className="mv-input" value={c.headline || ""} onChange={(e) => set("benefits.headline", e.target.value)} /></Field>
      <ListEditor
        items={items} testid="benefits"
        onAdd={() => set("benefits.items", [...items, { stat: "+10%", label: "Vorteil", description: "..." }])}
        onRemove={(i) => set("benefits.items", items.filter((_, k) => k !== i))}
        renderRow={(it, i) => (
          <div className="grid sm:grid-cols-3 gap-2">
            <input placeholder="Statistik (z. B. +22%)" className="mv-input" value={it.stat || ""} onChange={(e) => set(`benefits.items.${i}.stat`, e.target.value)} />
            <input placeholder="Label" className="mv-input sm:col-span-2" value={it.label || ""} onChange={(e) => set(`benefits.items.${i}.label`, e.target.value)} />
            <textarea placeholder="Beschreibung" className="mv-input sm:col-span-3 min-h-[60px]" value={it.description || ""} onChange={(e) => set(`benefits.items.${i}.description`, e.target.value)} />
          </div>
        )}
      />
    </div>
  );
}

function PricingTeaserEditor({ c, set }) {
  return (
    <div className="space-y-4">
      <Field label="Eyebrow"><input className="mv-input" value={c.eyebrow || ""} onChange={(e) => set("pricing.eyebrow", e.target.value)} /></Field>
      <Field label="Headline"><input className="mv-input" value={c.headline || ""} onChange={(e) => set("pricing.headline", e.target.value)} /></Field>
      <Field label="Subline"><textarea className="mv-input min-h-[60px]" value={c.subline || ""} onChange={(e) => set("pricing.subline", e.target.value)} /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="CTA-Label"><input className="mv-input" value={c.cta_label || ""} onChange={(e) => set("pricing.cta_label", e.target.value)} /></Field>
        <Field label="CTA-URL"><input className="mv-input font-mono text-sm" value={c.cta_url || ""} onChange={(e) => set("pricing.cta_url", e.target.value)} /></Field>
      </div>
    </div>
  );
}

function TestimonialsEditor({ c, set, uploadImage }) {
  const items = c.items || [];
  return (
    <div className="space-y-4">
      <Field label="Eyebrow"><input className="mv-input" value={c.eyebrow || ""} onChange={(e) => set("testimonials.eyebrow", e.target.value)} /></Field>
      <Field label="Headline"><input className="mv-input" value={c.headline || ""} onChange={(e) => set("testimonials.headline", e.target.value)} /></Field>
      <ListEditor
        items={items} testid="testimonials"
        onAdd={() => set("testimonials.items", [...items, { name: "Neuer Kunde", role: "Inhaber", salon: "", quote: "" }])}
        onRemove={(i) => set("testimonials.items", items.filter((_, k) => k !== i))}
        renderRow={(it, i) => (
          <div className="space-y-2">
            <div className="grid sm:grid-cols-3 gap-2">
              <input placeholder="Name" className="mv-input" value={it.name || ""} onChange={(e) => set(`testimonials.items.${i}.name`, e.target.value)} />
              <input placeholder="Rolle" className="mv-input" value={it.role || ""} onChange={(e) => set(`testimonials.items.${i}.role`, e.target.value)} />
              <input placeholder="Salon / Studio" className="mv-input" value={it.salon || ""} onChange={(e) => set(`testimonials.items.${i}.salon`, e.target.value)} />
            </div>
            <textarea placeholder="Zitat" className="mv-input min-h-[70px]" value={it.quote || ""} onChange={(e) => set(`testimonials.items.${i}.quote`, e.target.value)} />
            <ImageInput value={it.avatar_url} onChange={(v) => set(`testimonials.items.${i}.avatar_url`, v)} uploadImage={(f) => uploadImage(f, `testimonials.items.${i}.avatar_url`)} small />
          </div>
        )}
      />
    </div>
  );
}

function FaqEditor({ c, set }) {
  const items = c.items || [];
  return (
    <div className="space-y-4">
      <Field label="Eyebrow"><input className="mv-input" value={c.eyebrow || ""} onChange={(e) => set("faq.eyebrow", e.target.value)} /></Field>
      <Field label="Headline"><input className="mv-input" value={c.headline || ""} onChange={(e) => set("faq.headline", e.target.value)} /></Field>
      <ListEditor
        items={items} testid="faq"
        onAdd={() => set("faq.items", [...items, { question: "Neue Frage", answer: "" }])}
        onRemove={(i) => set("faq.items", items.filter((_, k) => k !== i))}
        renderRow={(it, i) => (
          <div className="space-y-2">
            <input placeholder="Frage" className="mv-input" value={it.question || ""} onChange={(e) => set(`faq.items.${i}.question`, e.target.value)} />
            <textarea placeholder="Antwort" className="mv-input min-h-[70px]" value={it.answer || ""} onChange={(e) => set(`faq.items.${i}.answer`, e.target.value)} />
          </div>
        )}
      />
    </div>
  );
}

function CtaFinalEditor({ c, set }) {
  return (
    <div className="space-y-4">
      <Field label="Eyebrow"><input className="mv-input" value={c.eyebrow || ""} onChange={(e) => set("cta_final.eyebrow", e.target.value)} /></Field>
      <Field label="Headline"><input className="mv-input" value={c.headline || ""} onChange={(e) => set("cta_final.headline", e.target.value)} /></Field>
      <Field label="Subline"><textarea className="mv-input min-h-[60px]" value={c.subline || ""} onChange={(e) => set("cta_final.subline", e.target.value)} /></Field>
      <div className="grid sm:grid-cols-2 gap-3">
        <Field label="CTA-Label"><input className="mv-input" value={c.cta_label || ""} onChange={(e) => set("cta_final.cta_label", e.target.value)} /></Field>
        <Field label="CTA-URL"><input className="mv-input font-mono text-sm" value={c.cta_url || ""} onChange={(e) => set("cta_final.cta_url", e.target.value)} /></Field>
      </div>
    </div>
  );
}

function FooterEditor({ c, set }) {
  const links = c.links || [];
  return (
    <div className="space-y-4">
      <Field label="Copyright-Zeile"><input className="mv-input" value={c.copyright || ""} onChange={(e) => set("footer.copyright", e.target.value)} /></Field>
      <ListEditor
        items={links} testid="footer-links"
        onAdd={() => set("footer.links", [...links, { label: "", url: "" }])}
        onRemove={(i) => set("footer.links", links.filter((_, k) => k !== i))}
        renderRow={(it, i) => (
          <div className="grid sm:grid-cols-2 gap-2">
            <input placeholder="Label" className="mv-input" value={it.label || ""} onChange={(e) => set(`footer.links.${i}.label`, e.target.value)} />
            <input placeholder="URL" className="mv-input font-mono text-sm" value={it.url || ""} onChange={(e) => set(`footer.links.${i}.url`, e.target.value)} />
          </div>
        )}
      />
    </div>
  );
}

/* ---------- shared helpers ---------- */

function ImageInput({ value, onChange, uploadImage, small = false }) {
  const ref = useRef(null);
  return (
    <div className="flex items-center gap-3">
      {value && <img src={value} alt="" style={{ width: small ? 48 : 80, height: small ? 48 : 80, borderRadius: 8, objectFit: "cover", border: "1px solid var(--mv-border)" }} />}
      <input type="file" accept="image/*" ref={ref} className="hidden" onChange={(e) => { const f = e.target.files?.[0]; if (f) uploadImage(f); }} />
      <button type="button" onClick={() => ref.current?.click()} className="mv-btn-ghost text-sm">
        <Upload className="w-3.5 h-3.5" strokeWidth={1.5} /> Bild hochladen
      </button>
      {value && (
        <button type="button" onClick={() => onChange("")} className="text-xs hover:underline" style={{ color: "var(--mv-text-muted)" }}>
          entfernen
        </button>
      )}
    </div>
  );
}

function ListEditor({ items, onAdd, onRemove, renderRow, testid }) {
  return (
    <div className="space-y-3" data-testid={`list-${testid}`}>
      {items.map((it, i) => (
        <div key={i} className="p-3 rounded-lg border" style={{ borderColor: "var(--mv-border)", background: "var(--mv-surface)" }}>
          <div className="flex justify-end mb-2">
            <button onClick={() => onRemove(i)} data-testid={`${testid}-remove-${i}`} className="p-1.5 rounded hover:bg-red-50" title="Entfernen">
              <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "#b91c1c" }} />
            </button>
          </div>
          {renderRow(it, i)}
        </div>
      ))}
      <button onClick={onAdd} data-testid={`${testid}-add`} className="mv-btn-ghost text-sm">
        <Plus className="w-3.5 h-3.5" strokeWidth={1.5} /> Eintrag hinzufügen
      </button>
    </div>
  );
}

/* ===== Custom Blocks editor (Elementor-style) ===== */

function newBlockId() { return Math.random().toString(36).slice(2, 11); }

function BlocksEditor({ blocks, set, uploadImage }) {
  const [templateOpen, setTemplateOpen] = useState(false);
  const addBlock = (type) => {
    const next = [...blocks, { id: newBlockId(), type, props: { ...(DEFAULTS[type] || {}) } }];
    set("blocks", next);
  };
  const insertTemplate = (tplBlocks) => set("blocks", [...blocks, ...tplBlocks]);
  const removeBlock = (i) => set("blocks", blocks.filter((_, k) => k !== i));
  const moveBlock = (i, dir) => {
    const j = i + dir;
    if (j < 0 || j >= blocks.length) return;
    const next = [...blocks];
    [next[i], next[j]] = [next[j], next[i]];
    set("blocks", next);
  };

  return (
    <div className="space-y-4" data-testid="blocks-editor">
      <p className="text-xs" style={{ color: "var(--mv-text-muted)" }}>
        Eigene Blöcke werden zwischen FAQ und Final-CTA gerendert. Nutze „Bild Full-Width&ldquo; für edge-to-edge Hero-Bilder mit Overlay-Text.
      </p>

      <div className="flex items-center gap-2 flex-wrap">
        <button
          type="button"
          data-testid="open-template-picker"
          onClick={() => setTemplateOpen(true)}
          className="mv-btn text-xs"
        >
          <Sparkles className="w-3.5 h-3.5" strokeWidth={1.75} /> Vorlagen einfügen
        </button>
        <span className="text-xs" style={{ color: "var(--mv-text-muted)" }}>oder einzelnen Block hinzufügen:</span>
      </div>

      <div className="flex flex-wrap gap-2">
        {COMPONENT_TYPES.map((c) => (
          <button
            key={c.type}
            type="button"
            data-testid={`add-block-${c.type}`}
            onClick={() => addBlock(c.type)}
            className="mv-btn-ghost text-xs"
          >
            <Plus className="w-3 h-3" strokeWidth={1.5} /> {c.label}
          </button>
        ))}
      </div>

      <TemplatePicker open={templateOpen} onClose={() => setTemplateOpen(false)} onInsert={insertTemplate} />

      <div className="space-y-3 mt-3">
        {blocks.map((b, i) => (
          <div key={b.id || i} className="p-3 rounded-lg border" style={{ borderColor: "var(--mv-border)", background: "var(--mv-surface)" }} data-testid={`block-${i}`}>
            <div className="flex items-center justify-between mb-3">
              <p className="text-xs font-mono" style={{ color: "var(--mv-text-muted)" }}>#{i + 1} · {b.type}</p>
              <div className="flex gap-1">
                <button onClick={() => moveBlock(i, -1)} disabled={i === 0} data-testid={`block-up-${i}`} className="p-1.5 rounded hover:bg-white disabled:opacity-30" title="Nach oben">
                  <ArrowUp className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                <button onClick={() => moveBlock(i, +1)} disabled={i === blocks.length - 1} data-testid={`block-down-${i}`} className="p-1.5 rounded hover:bg-white disabled:opacity-30" title="Nach unten">
                  <ArrowDown className="w-3.5 h-3.5" strokeWidth={1.5} />
                </button>
                <button onClick={() => removeBlock(i)} data-testid={`block-remove-${i}`} className="p-1.5 rounded hover:bg-red-50" title="Entfernen">
                  <Trash2 className="w-3.5 h-3.5" strokeWidth={1.5} style={{ color: "#b91c1c" }} />
                </button>
              </div>
            </div>
            <BlockPropsEditor
              block={b}
              setProp={(key, val) => set(`blocks.${i}.props.${key}`, val)}
              uploadFor={(field) => (f) => uploadImage(f, `blocks.${i}.props.${field}`)}
            />
          </div>
        ))}
        {blocks.length === 0 && (
          <p className="text-sm py-6 text-center" style={{ color: "var(--mv-text-muted)" }}>
            Noch keine eigenen Blöcke. Wähle oben einen Typ aus.
          </p>
        )}
      </div>
    </div>
  );
}

function BlockPropsEditor({ block, setProp, uploadFor }) {
  const t = block.type;
  const p = block.props || {};
  if (t === "heading") return (
    <div className="space-y-2">
      <input className="mv-input" placeholder="Überschrift-Text" value={p.text || ""} onChange={(e) => setProp("text", e.target.value)} />
      <div className="grid grid-cols-2 gap-2">
        <select className="mv-input" value={p.level || "h2"} onChange={(e) => setProp("level", e.target.value)}>
          <option value="h1">H1</option><option value="h2">H2</option><option value="h3">H3</option><option value="h4">H4</option>
        </select>
        <select className="mv-input" value={p.align || "left"} onChange={(e) => setProp("align", e.target.value)}>
          <option value="left">Links</option><option value="center">Zentriert</option><option value="right">Rechts</option>
        </select>
      </div>
    </div>
  );
  if (t === "text") return (
    <div className="space-y-2">
      <textarea className="mv-input min-h-[80px]" value={p.text || ""} onChange={(e) => setProp("text", e.target.value)} />
      <select className="mv-input" value={p.align || "left"} onChange={(e) => setProp("align", e.target.value)}>
        <option value="left">Links</option><option value="center">Zentriert</option><option value="right">Rechts</option>
      </select>
    </div>
  );
  if (t === "image") return (
    <div className="space-y-2">
      <ImageInput value={p.src} onChange={(v) => setProp("src", v)} uploadImage={uploadFor("src")} />
      <input className="mv-input" placeholder="Alt-Text" value={p.alt || ""} onChange={(e) => setProp("alt", e.target.value)} />
      <input className="mv-input" placeholder="Bildunterschrift (optional)" value={p.caption || ""} onChange={(e) => setProp("caption", e.target.value)} />
    </div>
  );
  if (t === "image_fullwidth") return (
    <div className="space-y-2">
      <ImageInput value={p.src} onChange={(v) => setProp("src", v)} uploadImage={uploadFor("src")} />
      <input className="mv-input" placeholder="Alt-Text" value={p.alt || ""} onChange={(e) => setProp("alt", e.target.value)} />
      <input className="mv-input" placeholder="Overlay-Titel (optional)" value={p.overlay_title || ""} onChange={(e) => setProp("overlay_title", e.target.value)} />
      <input className="mv-input" placeholder="Overlay-Untertitel (optional)" value={p.overlay_subtitle || ""} onChange={(e) => setProp("overlay_subtitle", e.target.value)} />
      <div className="grid grid-cols-3 gap-2">
        <div>
          <label className="block text-[11px] mb-1">Höhe (px)</label>
          <input type="number" className="mv-input" value={p.height || 480} onChange={(e) => setProp("height", parseInt(e.target.value) || 480)} />
        </div>
        <div>
          <label className="block text-[11px] mb-1">Overlay-Dunkelheit (0–1)</label>
          <input type="number" min="0" max="1" step="0.05" className="mv-input" value={p.overlay_opacity ?? 0.35} onChange={(e) => setProp("overlay_opacity", parseFloat(e.target.value))} />
        </div>
        <div>
          <label className="block text-[11px] mb-1">Text-Ausrichtung</label>
          <select className="mv-input" value={p.text_align || "center"} onChange={(e) => setProp("text_align", e.target.value)}>
            <option value="left">Links</option><option value="center">Zentriert</option><option value="right">Rechts</option>
          </select>
        </div>
      </div>
    </div>
  );
  if (t === "button") return (
    <div className="grid grid-cols-2 gap-2">
      <input className="mv-input" placeholder="Label" value={p.label || ""} onChange={(e) => setProp("label", e.target.value)} />
      <input className="mv-input font-mono text-sm" placeholder="URL" value={p.url || ""} onChange={(e) => setProp("url", e.target.value)} />
    </div>
  );
  if (t === "spacer") return (
    <div>
      <label className="block text-[11px] mb-1">Höhe (px)</label>
      <input type="number" className="mv-input" value={p.height || 48} onChange={(e) => setProp("height", parseInt(e.target.value) || 48)} />
    </div>
  );
  if (t === "hero") return (
    <div className="space-y-2">
      <input className="mv-input" placeholder="Titel" value={p.title || ""} onChange={(e) => setProp("title", e.target.value)} />
      <input className="mv-input" placeholder="Untertitel" value={p.subtitle || ""} onChange={(e) => setProp("subtitle", e.target.value)} />
      <div className="grid grid-cols-2 gap-2">
        <input className="mv-input" placeholder="CTA Label" value={p.cta_label || ""} onChange={(e) => setProp("cta_label", e.target.value)} />
        <input className="mv-input font-mono text-sm" placeholder="CTA URL" value={p.cta_url || ""} onChange={(e) => setProp("cta_url", e.target.value)} />
      </div>
      <ImageInput value={p.image} onChange={(v) => setProp("image", v)} uploadImage={uploadFor("image")} />
    </div>
  );
  // Fallback: raw JSON
  return (
    <div>
      <label className="block text-[11px] mb-1">Block-Properties (JSON)</label>
      <textarea
        className="mv-input min-h-[100px] font-mono text-xs"
        value={JSON.stringify(p, null, 2)}
        onChange={(e) => {
          try { const v = JSON.parse(e.target.value); Object.keys(v).forEach((k) => setProp(k, v[k])); } catch { /* ignore */ }
        }}
      />
    </div>
  );
}
